// src/services/VideoRoomService.js
import useJwt from '@/auth/jwt/useJwt'; // API çağrıları için
import AgoraRTC from 'agora-rtc-sdk-ng'; // Agora için

// Fallback avatarı burada tanımlayabiliriz veya component'ten alabiliriz
const fallbackAvatar = require('@/assets/images/auth/female-avatar.png')

/**
 * Verilen kanal ID'si için yayıncıları (host ve speakers) alır ve işler.
 * @param {string|number} channelId - Kanalın ID'si.
 * @param {string} mainPublisherUuid - Ana yayıncının UUID'si.
 * @returns {Promise<{mainHost: object|null, speakers: Array<object>}>} İşlenmiş host ve speaker listesi. Hata durumunda hata fırlatır.
 */
export async function getInitialSpeakersData(channelId, mainPublisherUuid) {

  if (!channelId || !mainPublisherUuid) {
    console.error('[VideoRoomService] channelId or mainPublisherUuid is missing.');
    throw new Error('Kanal bilgileri eksik.') // Component'in yakalaması için hata fırlat
  }

  try {
    // Log received parameters
    console.log(`[VideoRoomService] getInitialSpeakersData - Received channelId: ${channelId}, mainPublisherUuid: ${mainPublisherUuid}`)
    const response = await useJwt.getVideoRoomPublishers()

    // Log API response body
    console.log('[VideoRoomService] getInitialSpeakersData - API Response Body:', JSON.stringify(response?.data?.body));

    // Adjusted logic for response structure: Array with one object containing host/room details
    if (response && response.data && Array.isArray(response.data.body) && response.data.body.length > 0) {
      const hostAndRoomData = response.data.body[0]; // Get the first (and likely only) element

      let processedHost = null;
      const processedSpeakers = []; // Initialize speakers as empty, as this endpoint only gives host

      // Verify the fetched data corresponds to the expected mainPublisherUuid
      if (hostAndRoomData && hostAndRoomData.uuid === mainPublisherUuid) {
        // Log that the host was found and matched
        console.log('[VideoRoomService] getInitialSpeakersData - Found and matched hostData:', JSON.stringify(hostAndRoomData));

        processedHost = {
          id: hostAndRoomData.agoraUid, // Use userId if available, fallback needed?
          uuid: hostAndRoomData.uuid,
          name: hostAndRoomData.userName,
          avatar: hostAndRoomData.profile?.image || fallbackAvatar,
          isActive: true,
          isMuted: false, // Assuming default, might need update from Event
          level: hostAndRoomData.userBadgeLevel === 'boho_member' ? 10 : null,
          agoraUid: hostAndRoomData.agoraUid
        };
      } else {
        // Log if the UUID didn't match or data was missing
        console.warn(`[VideoRoomService] getInitialSpeakersData - Fetched data UUID (${hostAndRoomData?.uuid}) does not match expected mainPublisherUuid (${mainPublisherUuid}) or data is missing.`);
      }
      
      // Remove the old find/filter logic for 'publishers'
      // const hostData = publishers.find(p => p.uuid === mainPublisherUuid);
      // ... (removed hostData processing and speaker filtering)

      return { mainHost: processedHost, speakers: processedSpeakers };

    } else {
      console.error('[VideoRoomService] Invalid response format from getVideoRoomPublishers.');
      throw new Error('Konuşmacı listesi alınamadı (geçersiz format).');
    }

  } catch (error) {
    console.error('[VideoRoomService] Error fetching or processing publishers:', error);
    // Orijinal hatayı veya özel bir hata mesajını tekrar fırlatabiliriz
    throw new Error('Konuşmacı listesi alınırken sunucu hatası oluştu.');
  }
}

/**
 * Creates and initializes the Agora RTC client.
 * @param {Function} onUserPublished - Callback for 'user-published' event.
 * @param {Function} onUserUnpublished - Callback for 'user-unpublished' event.
 * @param {Function} onUserJoined - Callback for 'user-joined' event.
 * @param {Function} onUserLeft - Callback for 'user-left' event.
 * @returns {object} The initialized Agora RTC client instance.
 */
export function initializeAgoraClient(onUserPublished, onUserUnpublished, onUserJoined, onUserLeft) {
  const agoraClient = AgoraRTC.createClient({ mode: 'live', codec: 'vp8' });

  agoraClient.on('user-published', onUserPublished);
  agoraClient.on('user-unpublished', onUserUnpublished);
  agoraClient.on('user-joined', onUserJoined);
  agoraClient.on('user-left', onUserLeft);

  return agoraClient;
}

/**
 * Joins the Agora channel with the provided details.
 * @param {object} agoraClient - The initialized Agora RTC client.
 * @param {object} roomInfo - Contains channelName, rtcToken, etc.
 * @param {number|string} userIdentifier - The user ID for Agora.
 * @returns {Promise<number|string>} The UID assigned by Agora.
 * @throws Will throw an error if join fails.
 */
export async function joinAgoraChannel(agoraClient, roomInfo, userIdentifier) {
  const appId = process.env.VUE_APP_AGORA_ID;
  const { channelName, rtcToken } = roomInfo;

  if (!agoraClient || !appId || !channelName || !rtcToken || !userIdentifier) {
    console.error('[VideoRoomService] Missing required parameters for joining Agora channel.', { appId, channelName, rtcToken, userIdentifier, clientExists: !!agoraClient });
    throw new Error('Agora kanalına katılmak için gerekli bilgiler eksik.');
  }

  try {
    const uid = await agoraClient.join(
      appId,
      channelName,
      rtcToken,
      userIdentifier
    );

    return uid;
  } catch (error) {
    console.error('[VideoRoomService] Error joining Agora channel:', error);
    throw error; // Re-throw for the component to handle
  }
}

/**
 * Sets the client role in Agora.
 * @param {object} agoraClient - The Agora RTC client.
 * @param {'host'|'audience'} role - The desired role.
 * @returns {Promise<void>}
 * @throws Will throw an error if setting role fails.
 */
export async function setAgoraClientRole(agoraClient, role) {
  
  if (!agoraClient || (role !== 'host' && role !== 'audience')) {
      throw new Error('Invalid parameters for setting Agora role.');
  }
  try {
      await agoraClient.setClientRole(role);
      
  } catch (error) {
      console.error(`[VideoRoomService] Error setting Agora client role to ${role}:`, error);
      throw error;
  }
}

/**
 * Creates and publishes the local microphone audio track.
 * @param {object} agoraClient - The Agora RTC client (must be in 'host' role).
 * @returns {Promise<object>} The created and published local audio track.
 * @throws Will throw an error if track creation or publish fails.
 */
export async function publishLocalAudioTrack(agoraClient) {
  
  if (!agoraClient) {
    throw new Error('Agora client is required to publish audio track.');
  }
  try {
    const localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
    
    await agoraClient.publish(localAudioTrack);
    
    return localAudioTrack;
  } catch (error) {
    console.error('[VideoRoomService] Error creating or publishing local audio track:', error);
    throw error;
  }
}

/**
 * Creates and publishes the local camera video track.
 * @param {object} agoraClient - The Agora RTC client (must be in 'host' role).
 * @returns {Promise<object>} The created and published local video track.
 * @throws Will throw an error if track creation or publish fails.
 */
export async function publishLocalVideoTrack(agoraClient) {
  if (!agoraClient) {
    throw new Error('Agora client is required to publish video track.');
  }
  try {
    const localVideoTrack = await AgoraRTC.createCameraVideoTrack();
    await agoraClient.publish(localVideoTrack);
    console.log('[VideoRoomService] Local video track published successfully.');
    return localVideoTrack;
  } catch (error) {
    console.error('[VideoRoomService] Error creating or publishing local video track:', error);
    throw error; // Re-throw for the component to handle
  }
}

/**
 * Cleans up Agora resources (audio track and client connection).
 * @param {object|null} agoraClient - The Agora RTC client instance.
 * @param {object|null} localAudioTrack - The local audio track instance.
 * @param {object|null} localVideoTrack - The local video track instance.
 */
export async function leaveAgoraChannelService(agoraClient, localAudioTrack, localVideoTrack) {
  console.log('[VideoRoomService] leaveAgoraChannelService called.');
  if (localAudioTrack) {
    try {
      localAudioTrack.close();
      
    } catch (trackCloseError) {
        console.error('[VideoRoomService] Error closing local audio track:', trackCloseError);
    }
  }
  if (localVideoTrack) {
    try {
      localVideoTrack.close();
      console.log('[VideoRoomService] Local video track closed.');
    } catch (trackCloseError) {
        console.error('[VideoRoomService] Error closing local video track:', trackCloseError);
    }
  }
  if (agoraClient) {
    try {
      await agoraClient.leave();
      console.log('[VideoRoomService] Agora client left channel.');
    } catch (leaveError) {
      console.error('[VideoRoomService] Error leaving Agora channel via client:', leaveError);
      // Optionally re-throw or handle differently
    }
  }
}

/**
 * Ends the video room stream by calling the backend API and leaving the Agora channel.
 * @param {object|null} agoraClient - The Agora RTC client instance.
 * @param {object|null} localAudioTrack - The local audio track instance.
 * @param {object|null} localVideoTrack - The local video track instance.
 * @returns {Promise<boolean>} True if successful, false otherwise.
 */
export async function endStreamService(agoraClient, localAudioTrack, localVideoTrack) {
  console.log('[VideoRoomService] endStreamService called.');
  let treatAsSuccess = false; // Variable to store the intended outcome

  try {
    // 1. Call the backend API to signal the end
    console.log('[VideoRoomService] Calling useJwt.endVideoRoom...');
    const response = await useJwt.endVideoRoom();
    console.log('[VideoRoomService] useJwt.endVideoRoom response received:', response?.status);
    treatAsSuccess = true; // API call succeeded

  } catch (error) {
    console.error('[VideoRoomService] Error during endVideoRoom API call:', error.response?.data || error.message || error);

    // Check for the specific "already ended" error
    if (error.response?.data?.header?.error?.errorCode === '2922') {
      console.warn('[VideoRoomService] Stream already ended on backend (ErrorCode 2922). Treating as success for local cleanup.');
      treatAsSuccess = true; // Treat this specific error as success locally
    } else {
      // For any other API error, keep treatAsSuccess as false
      treatAsSuccess = false;
    }
  } finally {
    // 2. Leave the Agora channel - ALWAYS attempt this
    console.log('[VideoRoomService] Calling leaveAgoraChannelService in finally block...');
    try {
      await leaveAgoraChannelService(agoraClient, localAudioTrack, localVideoTrack);
      console.log('[VideoRoomService] leaveAgoraChannelService completed.');
    } catch (leaveError) {
      console.error('[VideoRoomService] Error during leaveAgoraChannelService in finally block:', leaveError);
      // Consider if Agora leave failure should force the overall result to false
      // For now, we let the API call result determine the success status.
      // treatAsSuccess = false; // Uncomment this if Agora leave failure is critical
    }
  }

  // Return the determined outcome after finally block execution
  return treatAsSuccess;
}

/**
 * Handles an audience member leaving the Agora channel.
 * Only leaves Agora, does not call backend endVideoRoom.
 * @param {object} agoraClient - The Agora RTC client instance.
 * @returns {Promise<boolean>} True if leaving Agora was successful, false otherwise.
 */
export async function leaveRoomAsAudienceService(agoraClient) {
  
  try {
    // Audience doesn't have a local audio track to close
    await leaveAgoraChannelService(agoraClient, null, null); // Pass null for localAudioTrack and localVideoTrack
    return true;
  } catch (error) {
    console.error('[VideoRoomService] Error during leaveRoomAsAudienceService:', error);
    return false;
  }
}

/**
 * Sends a heartbeat request to the backend for the given channel.
 * @param {string} channelName - The name of the channel for the heartbeat.
 * @returns {Promise<object|null>} The body of the heartbeat response on success, null on error.
 */
export async function sendHeartbeatRequest(channelName) {
  if (!channelName) {
    console.warn('[VideoRoomService] Skipping heartbeat: channelName not provided.');
    return null; // Still return null if channelName is missing
  }

  try {
    // Assuming useJwt is already imported in this file
    const response = await useJwt.sendVideoRoomHeartbeat(); // Does this need channelName? API definition shows no args
    const responseBody = response?.data?.body;
    
    // Consider adding a check for successful response structure if needed
    // if (!response || !response.data || typeof response.data.body === 'undefined') { 
    //   console.warn('[VideoRoomService] Heartbeat response format seems invalid.');
    //   // Decide how to handle invalid success response - maybe throw an error?
    // }

    return responseBody || null; // Return body or null if body is empty/undefined
  } catch (error) {
    console.error('[VideoRoomService] Error sending heartbeat:', error.response?.status || error.message || error); // Log status code if available
    // return null; // Indicate failure by returning null - REMOVED
    throw error; // Re-throw the original error
  }
}

/**
 * Kullanıcının konuşmacı olma isteğini API'ye gönderir.
 * @returns {Promise<boolean>} İstek başarılıysa true, değilse false döner veya hata fırlatır.
 */
export async function requestToBecomeSpeakerService() {

  try {
    // Check if useJwt itself exists and has the required method
    if (!useJwt || typeof useJwt.sendVideoRoomBePublisher !== 'function') {
        console.error('[VideoRoomService] useJwt instance or sendVideoRoomBePublisher method is not available.', useJwt);
        throw new Error('JWT Service veya gerekli API metodu bulunamadı.');
    }

    // Doğrudan useJwt üzerinden çağrı yap
    const response = await useJwt.sendVideoRoomBePublisher();

    // Başarı durumunu kontrol et (API'nin response yapısına göre değişebilir)
    // Örneğin, status 200 veya 204 ise başarılı kabul edilebilir.
    if (response && (response.status === 200 || response.status === 204 || response.data?.success)) {
      return true;
    } else {
      console.warn('[VideoRoomService] Request to become speaker API call did not indicate success:', response);
      // Belki burada da bir hata fırlatmak daha iyi olur
      throw new Error('Konuşmacı olma isteği gönderildi ancak sunucu başarıyı onaylamadı.');
    }
  } catch (error) {
    console.error('[VideoRoomService] Error requesting to become speaker:', error.response || error);
    // Hata mesajını tekrar fırlat
    // const errorMessage = error.response?.data?.message || error.message || 'Konuşmacı olma isteği gönderilirken bir hata oluştu.';
    // throw new Error(errorMessage);
    throw error; // Re-throw the original error object
  }
}

/**
 * Host'un bir konuşmacı isteğini onaylaması için API çağrısı yapar.
 * @param {string} userUuid Onaylanacak kullanıcının UUID'si.
 * @returns {Promise<boolean>} İstek başarılıysa true, değilse false döner veya hata fırlatır.
 */
export async function approveSpeakerRequestService(userUuid) {
  
  if (!userUuid) {
    console.error('[VideoRoomService] Cannot approve request: userUuid is missing.');
    throw new Error('Onaylanacak kullanıcı kimliği eksik.');
  }

  try {
    
    // jwtService içindeki fonksiyonun adı ve aldığı parametreler doğru olmalı
    const response = await useJwt.approveSpeakerRequestVideoRoom(userUuid); // Doğrudan useJwt üzerinden

    // Başarı durumunu kontrol et
    if (response && (response.status === 200 || response.status === 204 || response.data?.success)) {
      
      return true;
    } else {
      console.warn('[VideoRoomService] Approve speaker request API call did not indicate success:', response);
      throw new Error('Konuşmacı isteği onayı gönderildi ancak sunucu başarıyı onaylamadı.');
    }
  } catch (error) {
    console.error('[VideoRoomService] Error approving speaker request:', error.response || error);
    const errorMessage = error.response?.data?.message || error.message || 'Konuşmacı isteği onaylanırken bir hata oluştu.';
    throw new Error(errorMessage);
  }
}

/**
 * Host'un bir konuşmacı isteğini reddetmesi için API çağrısı yapar.
 * @param {string} userUuid Reddedilecek kullanıcının UUID'si.
 * @returns {Promise<boolean>} İstek başarılıysa true, değilse false döner veya hata fırlatır.
 */
export async function rejectSpeakerRequestService(userUuid) {
  
  if (!userUuid) {
    console.error('[VideoRoomService] Cannot reject request: userUuid is missing.');
    throw new Error('Reddedilecek kullanıcı kimliği eksik.');
  }

  try {
    
    // jwtService içindeki fonksiyonun adı ve aldığı parametreler doğru olmalı
    const response = await useJwt.rejectSpeakerRequestVideoRoom(userUuid); // Doğrudan useJwt üzerinden

    // Başarı durumunu kontrol et
    if (response && (response.status === 200 || response.status === 204 || response.data?.success)) {
      
      return true;
    } else {
      console.warn('[VideoRoomService] Reject speaker request API call did not indicate success:', response);
      throw new Error('Konuşmacı isteği reddi gönderildi ancak sunucu başarıyı onaylamadı.');
    }
  } catch (error) {
    console.error('[VideoRoomService] Error rejecting speaker request:', error.response || error);
    const errorMessage = error.response?.data?.message || error.message || 'Konuşmacı isteği reddedilirken bir hata oluştu.';
    throw new Error(errorMessage);
  }
}

/**
 * Handles the process of an audience member becoming a publisher (speaker).
 * Includes leaving the channel, rejoining with a new token, and publishing audio.
 * @param {object} agoraClient - The Agora RTC client instance.
 * @param {string} newToken - The new RTC token required for rejoining as a publisher.
 * @param {object} roomInfo - Current room information (channelName).
 * @param {number|string} userIdentifier - The user's identifier for Agora.
 * @returns {Promise<object|null>} The newly created local audio track, or null if failed.
 * @throws Will throw an error if any step fails.
 */
export async function becomePublisherService(agoraClient, newToken, roomInfo, userIdentifier) {
  

  if (!agoraClient || !newToken || !roomInfo || !userIdentifier) {
    console.error('[VideoRoomService] Missing required parameters for becomePublisherService.');
    throw new Error('Konuşmacı olmak için gerekli bilgiler eksik.');
  }

  try {
    // 1. Leave the current channel
    
    await agoraClient.leave();
    
    // 2. Rejoin with the new token
    await joinAgoraChannel(agoraClient, { ...roomInfo, rtcToken: newToken }, userIdentifier);

    // 3. Set role to host (implicitly required before publishing)
    await setAgoraClientRole(agoraClient, 'host');

    // 4. Publish the local audio track
    const newLocalAudioTrack = await publishLocalAudioTrack(agoraClient);
    return newLocalAudioTrack;

  } catch (error) {
    console.error('[VideoRoomService] Error during becomePublisherService:', error);
    // Attempt to revert to audience role on failure?
    try {
      console.warn('[VideoRoomService] Attempting to set role back to audience after failure.');
      await setAgoraClientRole(agoraClient, 'audience');
    } catch (revertError) {
      console.error('[VideoRoomService] Failed to revert role to audience:', revertError);
    }
    throw error; // Re-throw the original error for the component
  }
}

// --- Participant List Fetching ---

/**
 * Fetches the list of participants (publishers and subscribers) for a given video room.
 * @param {string|number} channelId - The ID of the video room channel.
 * @param {string|number} publisherId - The ID of the publisher.
 * @returns {Promise<object>} The API response body containing mainPublisherId, publishers, and subscribers.
 * @throws Will throw an error if the API call fails or response is invalid.
 */
export async function getListOfVideoRoomParticipants(channelId, publisherId) {
  
  if (!channelId || !publisherId) {
    console.error('[VideoRoomService] Cannot fetch participants: channelId or publisherId is missing.');
    throw new Error('Katılımcı listesini almak için kanal kimliği veya yayıncı kimliği eksik.');
  }

  try {
    
    const response = await useJwt.getListOfVideoRoomParticipants(channelId, publisherId); // Pass channelId if needed by the API

    // Validate response structure: Expect body to be an array of participants
    if (response && response.data && Array.isArray(response.data.body)) { 
      console.log(`[VideoRoomService] getListOfVideoRoomParticipants - Returning body array:`, response.data.body);
      return response.data.body; // Return the array of participants directly
    } else {
      console.warn('[VideoRoomService] Fetched participants but response format is invalid (expected body array):', response.data);
      return []; // Return empty array on invalid format
    }
  } catch (error) {
    console.error('[VideoRoomService] Error fetching participants:', error.response || error);
    const errorMessage = error.response?.data?.message || error.message || 'Katılımcı listesi alınırken bir hata oluştu.';
    throw new Error(errorMessage);
  }
}

export const getVideoRoomGoalsService = async (channelId) => {
  if (!channelId) {
    console.error('[VideoRoomService] channelId is required to fetch goals.');
    throw new Error('Channel ID is missing.');
  }
  try {
    const response = await useJwt.getGoalsOfVideoRoom(channelId);
    
    // Check if the response and data exist and have the expected structure
    if (response && response.data.body && typeof response.data.body.streamTitle !== 'undefined' && Array.isArray(response.data.body.goals)) {
      return response.data.body; // Return the { streamTitle, goals } object
    } else {
      console.warn('[VideoRoomService] Invalid or unexpected response structure for getGoalsOfVideoRoom:', response);
      // Return a default structure or null to indicate failure without throwing
      return { streamTitle: 'Başlık Yüklenemedi', goals: [] }; 
    }
  } catch (error) {
    console.error('[VideoRoomService] Error fetching video room goals:', error);
    // Optionally map specific errors or re-throw
    const errorMessage = error.response?.data?.message || error.message || 'Oda hedefleri alınırken bir hata oluştu.';
    throw new Error(errorMessage); // Re-throwing for the action to handle if needed
  }
};

/**
 * Kicks a user from the video room using their UUID.
 * Only hosts should be able to call this.
 * @param {string} userUuid - The UUID of the user to kick.
 * @returns {Promise<boolean>} - True if the request was likely successful, false otherwise.
 */
export const kickUserService = async (userUuid) => {
  if (!userUuid) {
    console.error('[VideoRoomService - kickUserService] userUuid is required.');
    throw new Error('Kicklenecek kullanıcının UUID bilgisi eksik.'); // Or return false
  }
  
  try {
    // Assuming useJwt is correctly configured and has kickUserFromVideoRoom method
    const response = await useJwt.kickUserFromVideoRoom(userUuid);
    return true; // Indicate success
  } catch (error) {
    console.error(`[VideoRoomService - kickUserService] Error kicking user ${userUuid}:`, error);
    // Rethrow or handle the error, maybe return false
    // throw new Error(`Kullanıcı (${userUuid}) kicklenirken hata: ${error.message || 'Bilinmeyen sunucu hatası'}`);
    return false; // Indicate failure
  }
};

/**
 * Toggles the mute status of a remote user in a video room via backend API.
 * Should only be callable by the host.
 * @param {string|number} channelId - The ID of the video room channel.
 * @param {string} targetUserUuid - The UUID of the user whose mute state to toggle.
 * @returns {Promise<boolean>} - True if the request was likely successful, false otherwise.
 */
export const toggleRemoteMuteService = async (channelId, targetUserUuid) => {
  // Video streamde buna şimdilik gerek yok
  return true;
  if (!channelId || !targetUserUuid) {
    console.error('[VideoRoomService - toggleRemoteMuteService] channelId and targetUserUuid are required.');
    throw new Error('Mute durumu değiştirilecek oda veya kullanıcı bilgisi eksik.');
  }
  
  try {
    // Call the method added to useJwt
    const response = await useJwt.toggleMuteVideoRoom(channelId, targetUserUuid);
    
    // Adjust success check based on actual API response
    if (response && (response.status === 200 || response.data?.success)) {
         
         return true; // Indicate success
    } else {
         console.warn(`[VideoRoomService - toggleRemoteMuteService] Toggle mute API call did not indicate success for ${targetUserUuid}.`, response);
         return false;
    }
   
  } catch (error) {
    console.error(`[VideoRoomService - toggleRemoteMuteService] Error toggling mute for user ${targetUserUuid}:`, error.response?.data || error.message || error);
    // Rethrow or return false
    // throw new Error(`Kullanıcının (${targetUserUuid}) mute durumu değiştirilirken hata: ${error.message || 'Bilinmeyen sunucu hatası'}`);
    return false; // Indicate failure
  }
};

/**
 * Retrieves the list of users who sent gifts in a video room.
 * @param {string|number} channelId - The ID of the video room channel.
 * @param {string} uuid - The UUID of the user.
 * @returns {Promise<Array<object>>} A promise that resolves to an array of gift sender objects.
 * @throws Will throw an error if the API call fails.
 */
export async function getGiftSendersListService(channelId, uuid) {
  
  if (!channelId || !uuid) {
    throw new Error('Channel ID and UUID are required to fetch gift senders.');
  }
  try {
    // Call the correct endpoint function from useJwt, passing the primitive channelId directly
    const response = await useJwt.getReceivedGiftsVideoRoom(channelId, uuid); 
    
    // Assuming the list is directly in response.data or response.data.body
    if (response && response.data) {
        // Check if the actual data is nested within a 'body' property
        if (Array.isArray(response.data.body)) {
            return response.data.body;
        }
        // Check if the data is directly an array
        if (Array.isArray(response.data)) {
            return response.data;
        }
    }
    console.warn('[VideoRoomService] Invalid or empty response format for gift senders:', response.data);
    return []; // Return empty array for invalid format or no data
  } catch (error) {
    console.error(`[VideoRoomService] Error fetching gift senders list for channel ${channelId} and uuid ${uuid}:`, error);
    // Re-throw the error for the action to handle
    throw error;
  }
}

/**
 * Fetches the list of available gifts for the video room.
 * @returns {Promise<Array<object>>} A list of gift objects.
 * @throws Will throw an error if fetching or processing fails.
 */
export async function getGifts() {
  
  try {
    const response = await useJwt.getVideoRoomGifts();

    if (response && response.data && Array.isArray(response.data.body)) {
      const gifts = response.data.body.map(gift => ({
        productCode: gift.productCode,
        productId: gift.productId,
        label: gift.label,
        credits: gift.credits,
        image: gift.image,
        riv: gift.riv // Assuming riv is part of the gift data
      }));
      
      return gifts;
    } else {
      console.error('[VideoRoomService] Invalid response format from getVideoRoomGifts.'); // Keep error log
      throw new Error('Hediye listesi alınamadı (geçersiz format).');
    }
  } catch (error) {
    console.error('[VideoRoomService] Error fetching or processing gifts:', error); // Keep error log
    // Re-throw the error or a custom one
    throw new Error('Hediye listesi alınırken sunucu hatası oluştu.');
  }
}

/**
 * Sends a gift in the video room.
 * @param {object} giftData - Data for the gift to send (e.g., { productCode }).
 * @returns {Promise<object>} The response body from the API.
 * @throws Will throw an error if sending fails.
 */
export async function sendVideoRoomGift(giftData) {
  
  if (!giftData || !giftData.productCode) {
    throw new Error('Hediye göndermek için ürün kodu gerekli.');
  }
  try {
    const response = await useJwt.sendVideoRoomGift(giftData);
    
    // Check for successful response if necessary based on API design
    if (response && response.data) { // Basic check, adjust as needed
      return response.data.body; // Return body or relevant data
    } else {
      throw new Error('Hediye gönderme işlemi başarısız oldu (geçersiz yanıt).');
    }
  } catch (error) {
    console.error('[VideoRoomService] Error sending video room gift:', error); // Keep error log
    // Re-throw the error or a custom one
    throw error; // Let the component handle the error details
  }
}

/**
 * Subscribes the current user (as audience) to a video room.
 * @param {string|number} channelId - The ID of the channel to join.
 * @param {string|number} publisherAgoraUid - The Agora UID of the main publisher.
 * @returns {Promise<object>} A promise that resolves to the roomInfo object needed for the store.
 * @throws Will throw an error if the API call fails or returns an error code.
 */
export async function subscribeVideoRoomService(channelId, publisherAgoraUid) {
  console.log(`[VideoRoomService] Subscribing to channel ${channelId} for publisher ${publisherAgoraUid}`);
  if (!channelId || !publisherAgoraUid) {
    throw new Error('Channel ID and Publisher Agora UID are required to subscribe.');
  }

  try {
    const response = await useJwt.subscribeVideoRoom(channelId, publisherAgoraUid);
    console.log('[VideoRoomService] subscribeVideoRoom response:', response);

    // Check if the expected body exists (Only process if API call didn't throw error)
    if (response?.data?.body) {
      const apiData = response.data.body;
      const roomInfo = {
        channelId: apiData.channelId,
        channelName: apiData.channelName,
        agoraUid: apiData.agoraUid, // This is the SUBSCRIBER's Agora UID
        rtcToken: apiData.rtcToken,
        heartbeatIntervalInSecs: apiData.heartbeatIntervalInSecs,
        nudityCheckIntervalInSecs: apiData.nudityCheckIntervalInSecs,
        mainPublisherUuid: apiData.mainPublisherUuid,
        // Include any other necessary fields from apiData
        isHost: false, // Always false when subscribing this way
        // Add other fields returned by API if needed (psrInfo, premiumRoom etc.)
        psrInfo: apiData.psrInfo,
        premiumRoom: apiData.premiumRoom,
        multiStream: apiData.multiStream, // Assuming these are returned
        relayedUsers: apiData.relayedUsers, 
        privateRoom: apiData.privateRoom,
      };
      console.log('[VideoRoomService] Processed roomInfo for subscriber:', roomInfo);
      return roomInfo;
    } else {
      // Handle cases where response is successful (2xx) but body is missing/invalid
      console.error('[VideoRoomService] Invalid response body from subscribeVideoRoom:', response.data);
      throw new Error('Yayına katılırken geçersiz yanıt alındı.');
    }

  } catch (error) {
    // Handle network errors or errors thrown from the API call
    console.error('[VideoRoomService] Error in subscribeVideoRoomService:', error);

    // Check if it's an API error with the specific header structure
    if (error?.response?.data?.header?.error?.errorCode) {
      const errorInfo = error.response.data.header.error;
      console.error('[VideoRoomService] API returned an error on subscribe:', errorInfo);
      // Throw a new Error with the specific message from the API
      throw new Error(errorInfo.message || 'Yayına katılırken API\'den bir hata alındı.');
    }
    
    // For other errors (network, unexpected structure, etc.), re-throw or create a generic error
    // If error already has a message (like Axios default), use it, otherwise provide a default
    throw new Error(error?.message || 'Yayına katılırken beklenmedik bir sorun oluştu.');
  }
}

/**
 * Sends a video frame to the backend for nudity/child detection.
 * @param {string|number} channelId - The ID of the channel (used as call context).
 * @param {string|number} userId - The ID of the user whose frame is being checked.
 * @param {string|Blob|File} imageData - Captured frame data.
 * @param {string} mimeType - The MIME type of the image (e.g., 'image/png').
 * @returns {Promise<object>} The body of the API response.
 * @throws Will throw an error if the API call fails.
 */
export async function sendCheckNudity(channelId, userId, imageData, mimeType = 'image/png') {
  if (!channelId || !userId || !imageData) {
    console.error('[VideoRoomService] Missing required parameters for sendCheckNudity.');
    throw new Error('Nudity check için gerekli bilgiler eksik.');
  }

  const imageBlob = imageData instanceof Blob ? imageData : await dataUrlToResizedBlob(imageData, mimeType);
  const payload = new FormData();
  payload.append('call_id', channelId);
  payload.append('user_id', userId);
  payload.append('mime_type', imageBlob.type || mimeType);
  payload.append('image', imageBlob, `nudity-frame.${mimeExtension(imageBlob.type || mimeType)}`);

  try {
    // Assuming checkNudity exists in useJwt
    const response = await useJwt.checkNudity(payload);
    // Return the body for processing in the component
    return response?.data?.body;
  } catch (error) {
    console.error('[VideoRoomService] Error calling checkNudity API:', error.response?.data || error.message || error);
    // Re-throw the error for the component to handle
    throw error;
  }
}

function dataUrlToBlob(dataUrl, fallbackMimeType = 'image/png') {
  const parts = dataUrl.split(',');
  const meta = parts[0] || '';
  const base64 = parts[1] || '';
  const mimeMatch = meta.match(/data:([^;]+);base64/);
  const mimeType = mimeMatch ? mimeMatch[1] : fallbackMimeType;
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
}

async function dataUrlToResizedBlob(dataUrl, fallbackMimeType = 'image/png') {
  const image = await loadImage(dataUrl);
  const maxSize = 480;
  const ratio = Math.min(1, maxSize / image.naturalWidth, maxSize / image.naturalHeight);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.naturalWidth * ratio));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * ratio));

  const context = canvas.getContext('2d');
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const mimeType = fallbackMimeType === 'image/png' ? 'image/jpeg' : fallbackMimeType;
  return canvasToBlob(canvas, mimeType, 0.65);
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

function canvasToBlob(canvas, mimeType, quality) {
  return new Promise(resolve => {
    if (canvas.toBlob) {
      canvas.toBlob(blob => {
        resolve(blob || dataUrlToBlob(canvas.toDataURL(mimeType, quality), mimeType));
      }, mimeType, quality);
      return;
    }
    resolve(dataUrlToBlob(canvas.toDataURL(mimeType, quality), mimeType));
  });
}

function mimeExtension(mimeType) {
  if (mimeType === 'image/jpeg') return 'jpg';
  if (mimeType === 'image/webp') return 'webp';
  return 'png';
}
